import { createMemoryHistory, useBlocker, useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useSelector } from '@shared/store';
import { MBSentry } from '@shared/util/sentry';
import { Navigator } from './Navigator';
import { browserRouteMapping, megaModalRouteMapping } from './routeMapping';
import { RouteName, RouteParamMapping } from './routeParams';

export const megaModalHistory = createMemoryHistory();

// Pub-sub for main-app browser navigation events.
// Used by components (like MBMegaModal) that live outside <RouterProvider>
// and cannot call TanStack hooks directly.
type NavigationListener = () => void;
const navigationListeners = new Set<NavigationListener>();

export function subscribeToNavigation(listener: NavigationListener): () => void {
  navigationListeners.add(listener);
  return () => navigationListeners.delete(listener);
}

export function notifyNavigationListeners() {
  navigationListeners.forEach(l => l());
}

function getPathForRoute(routeName: RouteName, params: RouteParamMapping[RouteName]): string {
  return browserRouteMapping[routeName]?.(params as any) ?? '';
}

export function useNavigator(): Navigator {
  const tanstackNavigate = useNavigate();
  const router = useRouter();

  const navigate = useCallback<Navigator['navigate']>(
    (routeName, params) => {
      if (!!browserRouteMapping[routeName]) {
        tanstackNavigate({ to: getPathForRoute(routeName, params) });
        megaModalHistory.push('/');
        notifyNavigationListeners();
      } else if (!!megaModalRouteMapping[routeName]) {
        megaModalHistory.push(megaModalRouteMapping[routeName](params as any));
      } else {
        MBSentry.exception({ error: new Error(`invalid web route: ${routeName}`) });
      }
    },
    [tanstackNavigate],
  );

  const replace = useCallback<Navigator['replace']>(
    (routeName, params) => {
      if (!!browserRouteMapping[routeName]) {
        tanstackNavigate({ to: getPathForRoute(routeName, params), replace: true });
        megaModalHistory.push('/');
        notifyNavigationListeners();
      } else if (!!megaModalRouteMapping[routeName]) {
        megaModalHistory.replace(megaModalRouteMapping[routeName](params as any));
      } else {
        MBSentry.exception({ error: new Error(`invalid web route: ${routeName}`) });
      }
    },
    [tanstackNavigate],
  );

  const goBack = useCallback(() => {
    if (router.history.canGoBack()) {
      router.history.back();
    } else {
      tanstackNavigate({ to: '/', replace: true });
    }
  }, [router, tanstackNavigate]);

  return useMemo<Navigator>(
    () => ({
      navigate,
      replace,
      reset: navigate,
      goBack,
      openMenu: () => megaModalHistory.push('/menu'),
      closeMenu: () => megaModalHistory.push('/'),
    }),
    [goBack, navigate, replace],
  );
}

export function useNavigationFocus(): boolean {
  const hidden = useSelector(state => state.appState.isHidden);
  return !hidden;
}

export interface Location<R extends RouteName = RouteName> {
  name: R;
  params?: RouteParamMapping[R];
}

/**
 * Blocks navigation away from the current route until the callback allows it.
 * Return true to allow, false to block.
 */
export function useNavigationBeforeLeave(
  callback: (nextLocation: Location) => boolean | Promise<boolean>,
) {
  useBlocker({
    shouldBlockFn: async ({ next }) => {
      const nextLocation: Location = {
        name: 'Home',
        params: { ...next.params, ...next.search },
      };
      const allow = await Promise.resolve(callback(nextLocation));
      return !allow;
    },
    enableBeforeUnload: false,
  });
}
